import React, { useState, useEffect, useMemo } from 'react';
import './AdminCalendar.css';
import { fetchWithAuth, isSuperAdmin, getBranchId } from '../utils/auth';
import { API_URL } from '../config';

const TYPE_COLORS = {
  regular:  { bg: '#dbeafe', text: '#1e40af', dot: '#3b82f6' },
  holiday:  { bg: '#fee2e2', text: '#991b1b', dot: '#ef4444' },
  special:  { bg: '#fef3c7', text: '#92400e', dot: '#f59e0b' },
  event:    { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
  test:     { bg: '#ede9fe', text: '#5b21b6', dot: '#8b5cf6' },
  project:  { bg: '#fce7f3', text: '#9d174d', dot: '#ec4899' },
  online:   { bg: '#e0f2fe', text: '#075985', dot: '#0ea5e9' },
};

const BLANK_EVENT = { weekNumber: null, date: '', dateLabel: '', type: 'regular', description: '', termInfo: '' };

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

const AdminCalendar = () => {
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [editMode, setEditMode]         = useState(false);
  const [isAdding, setIsAdding]         = useState(false);
  const [editForm, setEditForm]         = useState({});
  const [saving, setSaving]             = useState(false);
  const [viewYear, setViewYear]         = useState(null);
  const [viewMonth, setViewMonth]       = useState(null);

  const superAdmin = isSuperAdmin();
  const defaultBranchId = getBranchId() || 1;
  const [branches, setBranches]         = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(defaultBranchId);

  // Which academic year's schedule is being viewed/edited -- every year's
  // events are kept as their own set of rows (see CalendarEvent's docstring),
  // so switching this never mixes two years together and never touches a
  // different year's data.
  const [years, setYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(null);

  // Class-timing session(s) for the selected branch -- not year-scoped
  // (these rarely change and apply regardless of which year is being viewed).
  const [editingBatchKey, setEditingBatchKey] = useState(null); // 'batchA' | 'batchB' | null
  const [batchForm, setBatchForm] = useState({ name: '', time: '' });
  const [savingBatch, setSavingBatch] = useState(false);

  useEffect(() => {
    if (superAdmin) {
      fetchWithAuth(`${API_URL}/api/admin/branches`)
        .then(r => r.json()).then(d => setBranches(d.branches || [])).catch(() => {});
    }
  }, [superAdmin]);

  useEffect(() => {
    fetchWithAuth(`${API_URL}/api/admin/calendar/years`)
      .then(r => r.json())
      .then(d => {
        const list = d.academic_years || [];
        setYears(list);
        const current = list.find(y => y.is_current) || list[0];
        if (current) setSelectedYearId(current.id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { if (selectedYearId) fetchCalendarData(); }, [selectedBranchId, selectedYearId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth(
        `${API_URL}/api/admin/calendar?branch_id=${selectedBranchId}&academic_year_id=${selectedYearId}`
      );
      if (res.ok) {
        const data = await res.json();
        setCalendarData(data);
        setError('');
        setSelectedIndex(null);
        setEditMode(false);
        setIsAdding(false);
        setEditingBatchKey(null);
        // Default to first month that has events
        if (data.schedule && data.schedule.length > 0) {
          const first = data.schedule[0].date;
          const [y, m] = first.split('-').map(Number);
          setViewYear(y);
          setViewMonth(m - 1); // 0-indexed
        } else {
          const now = new Date();
          setViewYear(now.getFullYear());
          setViewMonth(now.getMonth());
        }
      } else {
        setError('Failed to load calendar data');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Build date → {event, index} map
  const eventMap = useMemo(() => {
    if (!calendarData) return {};
    const map = {};
    calendarData.schedule.forEach((event, idx) => {
      map[event.date] = { event, idx };
    });
    return map;
  }, [calendarData]);

  // Calendar grid for current view month
  const gridDays = useMemo(() => {
    if (viewYear === null) return [];
    const firstDay = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      cells.push({ day: d, dateStr, ...eventMap[dateStr] });
    }
    return cells;
  }, [viewYear, viewMonth, eventMap]);

  const selectedEvent = selectedIndex !== null ? calendarData?.schedule[selectedIndex] : null;

  const handleCellClick = (cell) => {
    if (!cell) return;
    if (cell.idx !== undefined) {
      // Existing event -- view/edit it.
      setSelectedIndex(cell.idx);
      setIsAdding(false);
      setEditMode(false);
      setEditForm({});
    } else {
      // Empty date -- start adding a brand-new event here.
      setSelectedIndex(null);
      setIsAdding(true);
      setEditMode(true);
      setEditForm({ ...BLANK_EVENT, date: cell.dateStr });
    }
  };

  const handleEdit = () => {
    if (selectedEvent) { setEditForm({ ...selectedEvent }); setEditMode(true); }
  };

  const handleCancel = () => {
    setEditMode(false);
    setIsAdding(false);
    setEditForm({});
    if (isAdding) setSelectedIndex(null);
  };

  const handleChange = (field, value) => setEditForm(f => ({ ...f, [field]: value }));

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const qs = `branch_id=${selectedBranchId}&academic_year_id=${selectedYearId}`;
      const res = isAdding
        ? await fetchWithAuth(`${API_URL}/api/admin/calendar/event?${qs}`, { method: 'POST', body: JSON.stringify(editForm) })
        : await fetchWithAuth(`${API_URL}/api/admin/calendar/event/${selectedIndex}?${qs}`, { method: 'PUT', body: JSON.stringify(editForm) });
      const data = await res.json();
      if (res.ok && data.success) {
        // Index positions shift on add (and would on delete too), so just
        // reload the whole schedule rather than patching the array by hand.
        await fetchCalendarData();
      } else {
        setError(data.error || `Failed to ${isAdding ? 'add' : 'update'} event`);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (selectedIndex === null) return;
    if (!window.confirm(`Delete "${selectedEvent.description}" on ${selectedEvent.dateLabel}? This cannot be undone.`)) return;
    try {
      setSaving(true);
      setError('');
      const qs = `branch_id=${selectedBranchId}&academic_year_id=${selectedYearId}`;
      const res = await fetchWithAuth(`${API_URL}/api/admin/calendar/event/${selectedIndex}?${qs}`, { method: 'DELETE' });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchCalendarData();
      } else {
        setError(data.error || 'Failed to delete event');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const startEditBatch = (batchKey, existing) => {
    setEditingBatchKey(batchKey);
    setBatchForm(existing ? { name: existing.name, time: existing.time } : { name: '', time: '' });
    setError('');
  };

  const cancelEditBatch = () => {
    setEditingBatchKey(null);
    setBatchForm({ name: '', time: '' });
  };

  const saveBatch = async () => {
    if (!batchForm.name.trim() || !batchForm.time.trim()) {
      setError('Session name and time are required.');
      return;
    }
    setSavingBatch(true);
    setError('');
    try {
      const res = await fetchWithAuth(
        `${API_URL}/api/admin/calendar/batch/${editingBatchKey}?branch_id=${selectedBranchId}`,
        { method: 'PUT', body: JSON.stringify(batchForm) }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchCalendarData();
        cancelEditBatch();
      } else {
        setError(data.error || 'Failed to save session.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSavingBatch(false);
    }
  };

  const deleteBatch = async (batchKey) => {
    if (!window.confirm('Remove this class-timing session?')) return;
    setSavingBatch(true);
    setError('');
    try {
      const res = await fetchWithAuth(
        `${API_URL}/api/admin/calendar/batch/${batchKey}?branch_id=${selectedBranchId}`,
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchCalendarData();
      } else {
        setError(data.error || 'Failed to delete session.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSavingBatch(false);
    }
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
    setSelectedIndex(null); setEditMode(false); setIsAdding(false);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
    setSelectedIndex(null); setEditMode(false); setIsAdding(false);
  };

  if (loading) return <div className="admin-calendar-loading">Loading calendar data...</div>;
  if (!calendarData) return <div className="admin-calendar-error">Failed to load calendar data</div>;

  const typeColor = (type) => TYPE_COLORS[type] || TYPE_COLORS.regular;

  return (
    <div className="admin-calendar-container">
      {/* Header */}
      <div className="calendar-header">
        <div>
          <h1>Manage Calendar</h1>
          <span className="calendar-year-label">Academic Year: {calendarData.academicYear}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {years.length > 0 && (
            <select
              value={selectedYearId || ''}
              onChange={e => setSelectedYearId(Number(e.target.value))}
              className="branch-select"
            >
              {years.map(y => <option key={y.id} value={y.id}>{y.year}{y.is_current ? ' (current)' : ''}</option>)}
            </select>
          )}
          {superAdmin && branches.length > 0 && (
            <select
              value={selectedBranchId}
              onChange={e => setSelectedBranchId(Number(e.target.value))}
              className="branch-select"
            >
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {error && <div className="calendar-error-banner">{error}</div>}

      {/* Batch summary -- class-timing session(s) for whichever branch is
          selected above; not year-scoped, since these rarely change. */}
      <div className="batch-summary">
        {calendarData.batches.map(b => (
          editingBatchKey === b.batchKey ? (
            <div className="batch-card-small" key={b.batchKey} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input style={{ width: '90px' }} value={batchForm.name} placeholder="Name"
                onChange={e => setBatchForm(f => ({ ...f, name: e.target.value }))} />
              <input style={{ width: '160px' }} value={batchForm.time} placeholder="e.g. 9:00 AM - 10:50 AM"
                onChange={e => setBatchForm(f => ({ ...f, time: e.target.value }))} />
              <button className="btn-sm" onClick={saveBatch} disabled={savingBatch}>{savingBatch ? 'Saving…' : 'Save'}</button>
              <button className="btn-sm" onClick={cancelEditBatch} disabled={savingBatch}>Cancel</button>
            </div>
          ) : (
            <div className="batch-card-small" key={b.batchKey}>
              <strong>{b.name}:</strong> {b.time}{' '}
              <button className="btn-sm" onClick={() => startEditBatch(b.batchKey, b)}>Edit</button>{' '}
              <button className="btn-sm" onClick={() => deleteBatch(b.batchKey)} disabled={savingBatch}>Delete</button>
            </div>
          )
        ))}
        {editingBatchKey && !calendarData.batches.some(b => b.batchKey === editingBatchKey) && (
          <div className="batch-card-small" style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input style={{ width: '90px' }} value={batchForm.name} placeholder="Name"
              onChange={e => setBatchForm(f => ({ ...f, name: e.target.value }))} />
            <input style={{ width: '160px' }} value={batchForm.time} placeholder="e.g. 9:00 AM - 10:50 AM"
              onChange={e => setBatchForm(f => ({ ...f, time: e.target.value }))} />
            <button className="btn-sm" onClick={saveBatch} disabled={savingBatch}>{savingBatch ? 'Saving…' : 'Save'}</button>
            <button className="btn-sm" onClick={cancelEditBatch} disabled={savingBatch}>Cancel</button>
          </div>
        )}
        {!editingBatchKey && calendarData.batches.length < 2 && (
          <button
            className="btn-sm"
            onClick={() => startEditBatch(calendarData.batches.some(b => b.batchKey === 'batchA') ? 'batchB' : 'batchA', null)}
          >
            + Add Session
          </button>
        )}
        <div className="batch-card-small">
          <strong>Total Events:</strong> {calendarData.schedule.length} &nbsp;
          {Object.entries(
            calendarData.schedule.reduce((acc, e) => ({ ...acc, [e.type]: (acc[e.type]||0)+1 }), {})
          ).map(([type, count]) => (
            <span key={type} style={{ marginLeft: '8px', fontSize: '12px',
              background: typeColor(type).bg, color: typeColor(type).text,
              padding: '1px 7px', borderRadius: '10px' }}>
              {count} {type}
            </span>
          ))}
        </div>
      </div>

      {/* Main workspace */}
      <div className="calendar-workspace">
        {/* Left: month grid */}
        <div className="calendar-grid-panel">
          {/* Month nav */}
          <div className="month-nav">
            <button className="month-nav-btn" onClick={prevMonth}>‹</button>
            <span className="month-nav-label">{MONTHS[viewMonth]} {viewYear}</span>
            <button className="month-nav-btn" onClick={nextMonth}>›</button>
          </div>

          <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 8px' }}>
            Click a highlighted date to view/edit it, or any empty date to add a new event.
          </p>

          {/* Day headers */}
          <div className="cal-grid">
            {DAYS.map(d => (
              <div key={d} className="cal-day-header">{d}</div>
            ))}

            {gridDays.map((cell, i) => {
              if (!cell) return <div key={`empty-${i}`} className="cal-cell cal-cell-empty" />;
              const hasEvent = cell.idx !== undefined;
              const isSelected = (hasEvent && cell.idx === selectedIndex) || (isAdding && editForm.date === cell.dateStr);
              const tc = hasEvent ? typeColor(cell.event.type) : null;
              const isToday = cell.dateStr === new Date().toISOString().slice(0,10);

              return (
                <div
                  key={cell.dateStr}
                  className={`cal-cell ${hasEvent ? 'cal-cell-event' : ''} ${isSelected ? 'cal-cell-selected' : ''} ${isToday ? 'cal-cell-today' : ''}`}
                  onClick={() => handleCellClick(cell)}
                  style={isSelected && tc ? { background: tc.bg, borderColor: tc.dot } : {}}
                >
                  <span className={`cal-day-num ${isToday ? 'today-num' : ''}`}>{cell.day}</span>
                  {hasEvent && (
                    <>
                      <span
                        className="cal-type-dot"
                        style={{ background: tc.dot }}
                        title={cell.event.type}
                      />
                      <span className="cal-event-desc">{cell.event.description}</span>
                    </>
                  )}
                </div>
              );
            })}

            {/* Fill remaining cells to complete last row */}
            {Array.from({ length: (7 - (gridDays.length % 7)) % 7 }).map((_, i) => (
              <div key={`trail-${i}`} className="cal-cell cal-cell-empty" />
            ))}
          </div>

          {/* Type legend */}
          <div className="cal-legend">
            {Object.entries(TYPE_COLORS).map(([type, c]) => (
              <span key={type} className="legend-item">
                <span className="legend-dot" style={{ background: c.dot }} />
                {type}
              </span>
            ))}
          </div>
        </div>

        {/* Right: event detail / edit */}
        <div className="event-detail-panel">
          {!selectedEvent && !isAdding ? (
            <div className="no-selection">
              <div className="no-selection-icon">📅</div>
              <p>Click a highlighted date to view or edit its event, or an empty date to add one</p>
            </div>
          ) : (
            <>
              <div className="detail-header">
                <div>
                  <h2 className="detail-date">{isAdding ? 'New Event' : selectedEvent.dateLabel}</h2>
                  {!isAdding && selectedEvent.weekNumber && (
                    <span className="week-badge">Week {selectedEvent.weekNumber}</span>
                  )}
                  {!isAdding && (
                    <span className="type-badge" style={{
                      background: typeColor(selectedEvent.type).bg,
                      color: typeColor(selectedEvent.type).text,
                      marginLeft: '8px'
                    }}>{selectedEvent.type}</span>
                  )}
                </div>
                {!editMode ? (
                  <div className="edit-actions">
                    <button className="edit-button" onClick={handleEdit}>Edit</button>
                    <button className="cancel-button" onClick={handleDelete} disabled={saving}>Delete</button>
                  </div>
                ) : (
                  <div className="edit-actions">
                    <button className="save-button" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : (isAdding ? 'Add Event' : 'Save')}
                    </button>
                    <button className="cancel-button" onClick={handleCancel} disabled={saving}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              {!editMode ? (
                <div className="details-view">
                  <div className="detail-row"><label>Date</label><span>{selectedEvent.date}</span></div>
                  <div className="detail-row"><label>Description</label><span className="description-text">{selectedEvent.description}</span></div>
                  {selectedEvent.termInfo && (
                    <div className="detail-row"><label>Term Info</label><span>{selectedEvent.termInfo}</span></div>
                  )}
                </div>
              ) : (
                <div className="details-edit">
                  <div className="edit-field">
                    <label>Week Number</label>
                    <input type="number" value={editForm.weekNumber || ''}
                      onChange={e => handleChange('weekNumber', e.target.value ? parseInt(e.target.value) : null)} />
                  </div>
                  <div className="edit-field">
                    <label>Date</label>
                    <input type="date" value={editForm.date}
                      onChange={e => handleChange('date', e.target.value)} />
                  </div>
                  <div className="edit-field">
                    <label>Date Label</label>
                    <input type="text" value={editForm.dateLabel}
                      onChange={e => handleChange('dateLabel', e.target.value)} />
                  </div>
                  <div className="edit-field">
                    <label>Type</label>
                    <select value={editForm.type} onChange={e => handleChange('type', e.target.value)}>
                      {Object.keys(TYPE_COLORS).map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="edit-field">
                    <label>Description</label>
                    <textarea value={editForm.description} rows="4"
                      onChange={e => handleChange('description', e.target.value)} />
                  </div>
                  <div className="edit-field">
                    <label>Term Info</label>
                    <input type="text" value={editForm.termInfo || ''}
                      onChange={e => handleChange('termInfo', e.target.value)} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCalendar;
